import { useState, useContext, useEffect } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AssignmentIcon from "@mui/icons-material/Assignment"
import ScaleIcon from "@mui/icons-material/Scale"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import { handlePrintPesajeTicket, handlePrintPesajeTicketBluetooth } from "../utils/printPesajeTicketHelper"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const accordionStyles = {
  mb: 2,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&:before": { display: "none" },
}

const EditarPesajes = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo, infoHome } = useContext(GlobalContext)

  const pesoidViejo = state?.pesoid ?? ""

  // isEditable ahora rige ÚNICAMENTE para el campo "Estado"
  const [isEditable, setIsEditable] = useState(true)
  const [lockReason, setLockReason] = useState("")

  const [formData, setFormData] = useState({
    pesoid: "",
    loccodigo: "",
    balcodigo: "",
    procodigo: "",
    pronombre: "",
    invcodigo: "",
    artcodigo: "",
    artdescri: "",
    pesoprecio: 0.0,
    pesohumeporcen: 0.0,
    impid: "",
    pesoretenporcen: 0.0,
    pesocantbruta: 0.0,
    pesohumecantidad: 0.0,
    pesocantfinal: 0.0,
    pesovalbruto: 0.0,
    pesoretenvalor: 0.0,
    pesovaltot: 0.0,
    opid: "",
    pesoestado: "A",
  })

  const [detalles, setDetalles] = useState([])

  // 1. CARGA DEL PESAJE SELECCIONADO
  const { data: fetchedData, isLoading: isFetching } = useQuery({
    queryKey: ["getPesajeById", pesoidViejo],
    queryFn: async () => {
      const response = await api.post("/Pesajes/getByIdPesajes", { pesoid: pesoidViejo })
      let dataExtracted = response?.data
      if (dataExtracted?.data?.pesoid) dataExtracted = dataExtracted.data
      else if (dataExtracted?.data?.data?.pesoid) dataExtracted = dataExtracted.data.data
      return dataExtracted || {}
    },
    enabled: !!pesoidViejo,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (fetchedData && fetchedData.pesoid) {
      setFormData({
        pesoid: fetchedData.pesoid,
        loccodigo: fetchedData.loccodigo || "01",
        balcodigo: fetchedData.balcodigo || "",
        procodigo: fetchedData.procodigo || "",
        pronombre: fetchedData.pronombre || "",
        invcodigo: fetchedData.invcodigo || "",
        artcodigo: fetchedData.artcodigo || "",
        artdescri: fetchedData.artdescri || "",
        pesoprecio: fetchedData.pesoprecio || 0.0,
        pesohumeporcen: fetchedData.pesohumeporcen || 0.0,
        impid: fetchedData.impid || "",
        pesoretenporcen: fetchedData.pesoretenporcen || 0.0,
        pesocantbruta: fetchedData.pesocantbruta || 0.0,
        pesohumecantidad: fetchedData.pesohumecantidad || 0.0,
        pesocantfinal: fetchedData.pesocantfinal || 0.0,
        pesovalbruto: fetchedData.pesovalbruto || 0.0,
        pesoretenvalor: fetchedData.pesoretenvalor || 0.0,
        pesovaltot: fetchedData.pesovaltot || 0.0,
        opid: fetchedData.opid || "",
        pesoestado: fetchedData.pesoestado || "A",
        pesofecisys: fetchedData.pesofecisys || new Date().toISOString(),
      })
      setDetalles(fetchedData.detalles || [])

      // BLOQUEO POR REGLAS DE NEGOCIO (Bloquea la edición del estado)
      if (fetchedData.pesoestado === "I" || fetchedData.pesoestado === "N") {
        setIsEditable(false)
        setLockReason("El pesaje se encuentra Inactivo/Anulado.")
      } else if (fetchedData.opid && fetchedData.opid.trim() !== "") {
        setIsEditable(false)
        setLockReason(`El pesaje ya fue procesado y está atado a la Orden de Pago: ${fetchedData.opid}`)
      } else {
        setIsEditable(true)
        setLockReason("")
      }
    }
  }, [fetchedData])

  // 2. MUTACIÓN (Solo se envía para actualizar el estado)
  const { mutateAsync: SaveEdicion, isPending: isSaving } = useMutation({
    queryKey: ["isEditingPesaje"],
    fn: async (dataPayload) => (await api.post("/Pesajes/updatePesajes", dataPayload)).data,
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    await SaveEdicion({ ...formData, detalles })
  }

  const executePrint = async () => {
    try {
      // 1. Intentar Imprimir por Bluetooth Directo
      await handlePrintPesajeTicketBluetooth(formData, detalles, infoHome)
    } catch (error) {
      // 2. Fallback: Si cancela o usa Safari en iOS, generar PDF
      handlePrintPesajeTicket(formData, detalles, infoHome)
    }
  }

  // Búsqueda de botones habilitados para el usuario
  const actionGrabar = selectedMenuInfo?.data?.barraAcciones?.find((a) => a.acccaption === "GRABAR")
  const actionImprimir = selectedMenuInfo?.data?.barraAcciones?.find((a) => a.acccaption === "IMPRIMIR")

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
          {/* BOTÓN GRABAR: Solo si no hay Orden de Pago ni está anulado */}
          {actionGrabar && isEditable && (
            <Tooltip title={actionGrabar.acccaption}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || isFetching}
                sx={{ border: "1px solid #ddd", bgcolor: "white" }}
              >
                {getIconComponent(actionGrabar.accnameicono, actionGrabar.acctipoico)}
              </IconButton>
            </Tooltip>
          )}

          {/* BOTÓN IMPRIMIR TICKET */}
          {actionImprimir && (
            <Tooltip title={actionImprimir.acccaption}>
              <IconButton
                // --- CÓDIGO MODIFICADO AQUÍ ---
                onClick={executePrint}
                // ------------------------------
                disabled={isFetching || !formData.pesoid}
                sx={{ border: "1px solid #ddd", bgcolor: "white", color: "#196C87" }}
              >
                {getIconComponent(actionImprimir.accnameicono, actionImprimir.acctipoico)}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <div style={{ display: "flex", justifyContent: "center", mb: "30px", fontSize: "25px" }}>
          <b>Visualización y Edición de Pesaje</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isFetching} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          {!isEditable && !isFetching && lockReason !== "" && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <b>Edición Bloqueada:</b> {lockReason} Solo puede visualizar o imprimir.
            </Alert>
          )}

          {/* ACORDEON 1: DATOS GENERALES */}
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  DATOS GENERALES (Solo Lectura)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    disabled
                    fullWidth
                    label="Nº Pesaje"
                    InputLabelProps={{ shrink: true }}
                    value={formData.pesoid}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    disabled
                    fullWidth
                    label="Localidad"
                    InputLabelProps={{ shrink: true }}
                    value={formData.loccodigo}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    disabled
                    fullWidth
                    label="Balanza"
                    InputLabelProps={{ shrink: true }}
                    value={formData.balcodigo}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    disabled
                    fullWidth
                    label="Proveedor"
                    InputLabelProps={{ shrink: true }}
                    value={`${formData.procodigo} - ${formData.pronombre}`}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* ACORDEON 2: PRODUCTO Y LIQUIDACIÓN */}
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ReceiptLongIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  PRODUCTO Y CONDICIONES
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    disabled
                    fullWidth
                    label="Artículo"
                    InputLabelProps={{ shrink: true }}
                    value={`${formData.artcodigo} - ${formData.artdescri}`}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    disabled
                    type="number"
                    fullWidth
                    label="Precio Sugerido ($)"
                    InputLabelProps={{ shrink: true }}
                    value={formData.pesoprecio}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    disabled
                    type="number"
                    fullWidth
                    label="% Humedad"
                    InputLabelProps={{ shrink: true }}
                    value={formData.pesohumeporcen}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    disabled
                    fullWidth
                    label="Retención"
                    InputLabelProps={{ shrink: true }}
                    value={`${formData.impid} (${formData.pesoretenporcen}%)`}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />
                </Grid>

                {/* ESTE ES EL ÚNICO CAMPO QUE PUEDE SER EDITABLE */}
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    disabled={!isEditable}
                    fullWidth
                    label="Estado"
                    value={formData.pesoestado}
                    onChange={(e) => setFormData((prev) => ({ ...prev, pesoestado: e.target.value }))}
                  >
                    <MenuItem value="A">ACTIVO</MenuItem>
                    <MenuItem value="I">INACTIVO</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {/* CUADRO DE RESUMEN */}
              <Box sx={{ mt: 3, p: 2, bgcolor: "#e8f4f8", borderRadius: 2, border: "1px solid #b3d4e0" }}>
                <Typography variant="subtitle2" color="primary" mb={2}>
                  <b>RESUMEN DE LIQUIDACIÓN</b>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="textSecondary">
                      Lbs Brutas
                    </Typography>
                    <Typography fontWeight="bold">{formData.pesocantbruta}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="error">
                      (-) Lbs Humedad
                    </Typography>
                    <Typography fontWeight="bold" color="error">
                      {formData.pesohumecantidad}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="primary">
                      (=) Lbs Netas
                    </Typography>
                    <Typography fontWeight="bold" color="primary">
                      {formData.pesocantfinal}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="textSecondary">
                      Subtotal USD
                    </Typography>
                    <Typography fontWeight="bold">${formData.pesovalbruto}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="error">
                      (-) Retención USD
                    </Typography>
                    <Typography fontWeight="bold" color="error">
                      ${formData.pesoretenvalor}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="primary">
                      (=) TOTAL A PAGAR
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ${formData.pesovaltot}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ACORDEON 3: DETALLE DE PESOS */}
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ScaleIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  INGRESOS DE BÁSCULA (Solo Lectura)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "500px" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ width: "80px" }}>
                        <b># Secuencia</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Cantidad (Libras)</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles?.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="center" sx={{ bgcolor: "#fafafa" }}>
                          {row.pesosecuen || idx + 1}
                        </TableCell>
                        <TableCell padding="none" sx={{ px: 2 }}>
                          <TextField
                            disabled
                            type="number"
                            variant="standard"
                            fullWidth
                            inputProps={{ style: { textAlign: "center" } }}
                            value={row.pesocantidad}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {detalles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No hay pesos registrados.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarPesajes
